/**
 * Step 4 — review and submit. Also the landing page Stripe redirects back
 * to on a payment method that needs a full-page redirect (see
 * ApplicationFee's `return_url`), so this has to handle arriving here with
 * the fee already paid and arriving here without having paid yet, not just
 * the straight-line "click submit" case.
 *
 * Once submitted, `applications_applicant_update`'s USING clause hides the
 * row from any further UPDATE by this applicant — there is no edit path
 * back, by design (see rls-policies.sql). This page reflects that: past
 * DRAFT, it shows status only — and gets the arch mark back, the same
 * threshold moment as PreQualification's, because arriving here is the
 * flow's other doorway.
 */

import { useEffect, useState } from 'react';
import { useParams } from 'react-router';
import { api } from '../../lib/api.js';
import { FormErrors } from '../../components/Form.js';
import { ApplyProgress } from './ApplyProgress.js';

interface ApplicationDto {
  id: string;
  status: string;
  submittedAt: string | null;
}

const STATUS_COPY: Record<string, { eyebrow: string; title: string; body: string }> = {
  SUBMITTED: {
    eyebrow: 'Application received',
    title: "You're in.",
    body: "We've received your application and will be in touch. Applications are reviewed in the order they were submitted.",
  },
  APPROVED: {
    eyebrow: 'Welcome home',
    title: 'Congratulations.',
    body: 'A member of our team will reach out about next steps.',
  },
  DECLINED: {
    eyebrow: 'Application decided',
    title: "Here's where things stand.",
    body: 'A member of our team will follow up with details.',
  },
};

export function ApplicationSubmit() {
  const { id } = useParams<{ id: string }>();
  const [application, setApplication] = useState<ApplicationDto | null>(null);
  const [loadError, setLoadError] = useState<unknown>(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<unknown>(null);

  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    void (async () => {
      try {
        const app = await api<ApplicationDto>(`/applications/${id}`);
        if (!cancelled) setApplication(app);
      } catch (caught) {
        if (!cancelled) setLoadError(caught);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [id]);

  async function onSubmit() {
    if (!id) return;
    setSubmitting(true);
    setSubmitError(null);
    try {
      const app = await api<ApplicationDto>(`/applications/${id}/submit`, { method: 'POST' });
      setApplication(app);
    } catch (caught) {
      setSubmitError(caught);
    } finally {
      setSubmitting(false);
    }
  }

  if (loadError) {
    return (
      <section>
        <div className="card">
          <FormErrors error={loadError} />
        </div>
      </section>
    );
  }

  if (!application) {
    return <p className="muted">Loading…</p>;
  }

  if (application.status !== 'DRAFT') {
    const copy = STATUS_COPY[application.status] ?? {
      eyebrow: 'Application decided',
      title: 'Here’s where things stand.',
      body: '',
    };
    return (
      <section>
        <div className="apply-hero">
          <div className="apply-arch" />
          <p className="apply__eyebrow">{copy.eyebrow}</p>
          <h1>{copy.title}</h1>
          {copy.body ? <p className="apply__lede">{copy.body}</p> : null}
        </div>
      </section>
    );
  }

  return (
    <section>
      <ApplyProgress step={4} />

      <div className="apply-hero">
        <p className="apply__eyebrow">Last look</p>
        <h1>Review and submit.</h1>
        <p className="apply__lede">
          Once submitted, your application can no longer be edited — if something needs to change,
          contact the management office.
        </p>
      </div>

      <div className="card">
        <FormErrors error={submitError} />

        <button
          className="button"
          type="button"
          disabled={submitting}
          onClick={() => void onSubmit()}
        >
          {submitting ? 'Submitting…' : 'Submit application'}
        </button>
      </div>
    </section>
  );
}
